/**
 * Chart Component
 * Wrapper for chart libraries (recharts, chart.js, etc.)
 */

import { useState, useEffect } from 'react'

/**
 * Simple Bar Chart Component
 * Falls back to simple HTML/CSS if chart library not available
 */
export const BarChart = ({ data, xKey, yKey, title, height = 300 }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <p className="text-gray-500">No data available</p>
      </div>
    )
  }

  const maxValue = Math.max(...data.map((d) => d[yKey] || 0))

  return (
    <div className="space-y-2">
      {title && <h3 className="text-lg font-semibold">{title}</h3>}
      <div className="space-y-2" style={{ height }}>
        {data.map((item, index) => {
          const value = item[yKey] || 0
          const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0

          return (
            <div key={index} className="flex items-center gap-3">
              <div className="w-24 text-sm text-gray-600 truncate">{item[xKey]}</div>
              <div className="flex-1 relative">
                <div className="h-8 bg-gray-100 rounded overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded transition-all"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-700">
                  {value}
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Line Chart Component
 */
export const LineChart = ({ data, xKey, yKey, title, height = 300 }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <p className="text-gray-500">No data available</p>
      </div>
    )
  }

  const maxValue = Math.max(...data.map((d) => d[yKey] || 0))
  const minValue = Math.min(...data.map((d) => d[yKey] || 0))
  const range = maxValue - minValue || 1

  // Simple SVG line chart
  const points = data.map((item, index) => {
    const x = (index / (data.length - 1 || 1)) * 100
    const y = 100 - (((item[yKey] || 0) - minValue) / range) * 100
    return { x, y, value: item[yKey], label: item[xKey] }
  })

  const pathData = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ')

  return (
    <div className="space-y-2">
      {title && <h3 className="text-lg font-semibold">{title}</h3>}
      <div className="relative" style={{ height }}>
        <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none">
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map((y) => (
            <line
              key={y}
              x1="0"
              y1={y}
              x2="100"
              y2={y}
              stroke="#e5e7eb"
              strokeWidth="0.5"
            />
          ))}
          {/* Data line */}
          <path
            d={pathData}
            fill="none"
            stroke="#3b82f6"
            strokeWidth="2"
            className="drop-shadow-sm"
          />
          {/* Data points */}
          {points.map((point, index) => (
            <circle
              key={index}
              cx={point.x}
              cy={point.y}
              r="1.5"
              fill="#3b82f6"
              className="hover:r-2 transition-all"
            >
              <title>
                {point.label}: {point.value}
              </title>
            </circle>
          ))}
        </svg>
        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-gray-500">
          <span>{maxValue}</span>
          <span>{minValue}</span>
        </div>
        {/* X-axis labels */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-gray-500">
          {points
            .filter((_, i) => i % Math.ceil(points.length / 5) === 0 || i === points.length - 1)
            .map((point, i) => (
              <span key={i}>{point.label}</span>
            ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Pie Chart Component
 */
export const PieChart = ({ data, labelKey, valueKey, title }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">No data available</p>
      </div>
    )
  }

  const total = data.reduce((sum, item) => sum + (item[valueKey] || 0), 0)
  const colors = [
    '#3b82f6',
    '#8b5cf6',
    '#ec4899',
    '#f59e0b',
    '#10b981',
    '#ef4444',
    '#06b6d4',
    '#84cc16',
  ]

  let currentAngle = 0
  const segments = data.map((item, index) => {
    const value = item[valueKey] || 0
    const percentage = total > 0 ? (value / total) * 100 : 0
    const angle = (percentage / 100) * 360
    const startAngle = currentAngle
    currentAngle += angle

    return {
      ...item,
      percentage,
      startAngle,
      angle,
      color: colors[index % colors.length],
    }
  })

  return (
    <div className="space-y-4">
      {title && <h3 className="text-lg font-semibold">{title}</h3>}
      <div className="flex items-center gap-6">
        {/* Pie visualization */}
        <div className="relative w-48 h-48">
          <svg viewBox="0 0 100 100" className="w-full h-full">
            {segments.map((segment, index) => {
              const startAngleRad = (segment.startAngle * Math.PI) / 180
              const endAngleRad = ((segment.startAngle + segment.angle) * Math.PI) / 180
              const largeArc = segment.angle > 180 ? 1 : 0

              const x1 = 50 + 50 * Math.cos(startAngleRad)
              const y1 = 50 + 50 * Math.sin(startAngleRad)
              const x2 = 50 + 50 * Math.cos(endAngleRad)
              const y2 = 50 + 50 * Math.sin(endAngleRad)

              const pathData = [
                `M 50 50`,
                `L ${x1} ${y1}`,
                `A 50 50 0 ${largeArc} 1 ${x2} ${y2}`,
                `Z`,
              ].join(' ')

              return (
                <path
                  key={index}
                  d={pathData}
                  fill={segment.color}
                  stroke="white"
                  strokeWidth="2"
                  className="hover:opacity-80 transition-opacity"
                >
                  <title>
                    {segment[labelKey]}: {segment.value} ({segment.percentage.toFixed(1)}%)
                  </title>
                </path>
              )
            })}
          </svg>
        </div>

        {/* Legend */}
        <div className="flex-1 space-y-2">
          {segments.map((segment, index) => (
            <div key={index} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: segment.color }}
              />
              <span className="text-sm flex-1">{segment[labelKey]}</span>
              <span className="text-sm font-semibold">
                {segment[valueKey]} ({segment.percentage.toFixed(1)}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default {
  BarChart,
  LineChart,
  PieChart,
}

